// Brand primitives — template §3: "components/ui/ are brand primitives with no content."
//
// Grouped in one module because each is a handful of lines and they are always imported together.
// None knows anything about bookings, payments or documents; a primitive that knew about a domain
// would not be a primitive.

import { Link } from "react-router-dom";

// ── Button ───────────────────────────────────────────────────────────────────
export function Button({
  as = "button",
  to,
  variant = "primary",
  size,
  loading = false,
  disabled = false,
  className = "",
  children,
  ...rest
}) {
  const cls = [
    "btn",
    `btn-${variant}`,
    size ? `btn-${size}` : "",
    className,
  ].filter(Boolean).join(" ");

  const inner = loading ? (
    <>
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
      {children}
    </>
  ) : (
    children
  );

  if (as === "link" && to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {inner}
      </Link>
    );
  }

  return (
    <button className={cls} disabled={loading || disabled} {...rest}>
      {inner}
    </button>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
export function Card({ title, action, footer, className = "", bodyClassName = "", children }) {
  return (
    <div className={`card border-0 shadow-sm ${className}`}>
      {(title || action) && (
        <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between pt-3 pb-0">
          {title && <h2 className="h6 mb-0">{title}</h2>}
          {action}
        </div>
      )}
      <div className={`card-body ${bodyClassName}`}>{children}</div>
      {footer && <div className="card-footer bg-white border-top-0 pt-0">{footer}</div>}
    </div>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ tone = "secondary", children }) {
  return <span className={`badge text-bg-${tone}`}>{children}</span>;
}

// ── StatTile ─────────────────────────────────────────────────────────────────
export function StatTile({ label, value, hint, icon, to }) {
  const body = (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body py-3">
        <div className="d-flex align-items-start justify-content-between">
          <div className="stat-label">{label}</div>
          {icon && <i className={`bi ${icon} text-primary opacity-50`} />}
        </div>
        <div className="stat-value">{value}</div>
        {hint && <div className="stat-hint">{hint}</div>}
      </div>
    </div>
  );

  return to ? (
    <Link to={to} className="text-decoration-none text-body d-block h-100">
      {body}
    </Link>
  ) : (
    body
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
export function Skeleton({ width = "100%", height = 14, radius = 6, className = "" }) {
  return (
    <span
      className={`vp-skeleton ${className}`}
      aria-hidden="true"
      style={{ display: "block", width, height, borderRadius: radius }}
    />
  );
}

export function SkeletonList({ rows = 4, height = 62 }) {
  return (
    <div className="d-grid gap-2" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={height} radius={10} />
      ))}
    </div>
  );
}

export function SkeletonTiles({ count = 4, height = 88 }) {
  return (
    <div className="row g-3" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <div className="col-6 col-lg-3" key={i}>
          <Skeleton height={height} radius={12} />
        </div>
      ))}
    </div>
  );
}

// ── EmptyState ───────────────────────────────────────────────────────────────
export function EmptyState({ icon = "bi-inbox", title, hint, action }) {
  return (
    <div className="text-center text-muted py-5 px-3">
      <i className={`bi ${icon}`} style={{ fontSize: 30, opacity: 0.45 }} />
      <div className="mt-2 fw-semibold text-body">{title}</div>
      {hint && <div className="small mt-1">{hint}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// ── ErrorState ───────────────────────────────────────────────────────────────
// Part 6 (2026-10-07; owner Part 6 errors): ONE customer-safe error presentation, the Home's SectionError pattern
// (`vp-home-error`). The words come from the KIND of failure only — never the server's message, a status code or the
// transport's text ("Network Error", "Request failed with status code 500"). Form validation keeps its own approved
// messages through `errorMessage`; this is for a read that failed.
function errorStateWords(error) {
  const status = error?.response?.status;
  if (!error?.response) return "Please check your connection and try again.";
  if (status === 401) return "Please sign in again to continue.";
  if (status === 403 || status === 404) return "This is not available to your account.";
  if (status === 422) return "This is not available right now.";
  return "Something went wrong on our side. Please try again in a moment.";
}

export function ErrorState({ error, onRetry, title = "We could not load this" }) {
  const status = error?.response?.status;
  const soft = status === 403 || status === 404 || status === 422;
  return (
    <div className="vp-home-error vp-error-state" role="alert">
      <i className={`bi ${soft ? "bi-info-circle" : "bi-exclamation-circle"}`} aria-hidden="true" />
      <div className="flex-grow-1 min-w-0">
        <div className="fw-semibold">{soft ? "Not available" : title}</div>
        <div className="small">{errorStateWords(error)}</div>
        {onRetry && !soft && (
          <button type="button" className="btn btn-link btn-sm p-0 vp-touch" onClick={onRetry}>Try again</button>
        )}
      </div>
    </div>
  );
}

// ── Field ────────────────────────────────────────────────────────────────────
export function Field({ label, id, error, hint, required, children }) {
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={id}>
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {children}
      {hint && !error && <div id={id ? `${id}-hint` : undefined} className="form-text">{hint}</div>}
      {error && <div id={id ? `${id}-error` : undefined} className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

// ── DataRow — a label/value pair, the portal's most repeated shape ────────────
export function DataRow({ label, value, tone }) {
  return (
    <div className="d-flex justify-content-between align-items-start gap-3 py-2 border-bottom">
      <span className="text-muted small">{label}</span>
      <span className={`text-end fw-medium ${tone ? `text-${tone}` : ""}`}>{value}</span>
    </div>
  );
}
